import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const HOME_BY_ROLE = { ADMIN: "/admin/dashboard", FACULTY: "/faculty/dashboard", STUDENT: "/student/dashboard" };

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success(`Welcome back, ${user.name}`);
            const from = location.state?.from || HOME_BY_ROLE[user.role] || "/";
            // Force a full navigation so AuthContext re-bootstraps cleanly
            window.location.href = from;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            {/* Left: form */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="flex items-center gap-3 mb-12" data-testid="login-brand">
                        <div className="h-11 w-11 rounded-xl bg-brand-gradient flex items-center justify-center shadow-mx-md">
                            <span className="font-display text-white text-xl">M</span>
                        </div>
                        <div className="leading-tight">
                            <div className="font-display text-xl">MX CAMPUS</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Learning Hub</div>
                        </div>
                    </Link>

                    <h1 className="font-display text-4xl lg:text-5xl tracking-tight leading-tight">
                        Welcome back.
                    </h1>
                    <p className="mt-3 text-muted-foreground text-base">
                        Sign in to continue your learning journey on <span className="text-foreground font-semibold">MX Campus</span>.
                    </p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-widest font-bold">Email</Label>
                            <Input
                                id="email" type="email" required autoComplete="email"
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@mxacademy.in"
                                className="mt-2 h-12 rounded-xl"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <div className="flex justify-between items-center">
                                <Label htmlFor="password" className="text-xs uppercase tracking-widest font-bold">Password</Label>
                                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground" data-testid="forgot-password">
                                    Forgot password?
                                </Link>
                            </div>
                            <div className="relative mt-2">
                                <Input
                                    id="password" type={showPw ? "text" : "password"} required autoComplete="current-password"
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="h-12 rounded-xl pr-12"
                                    data-testid="login-password-input"
                                />
                                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="toggle-password">
                                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button type="submit" disabled={loading} className="mx-btn-primary h-12 w-full text-base" data-testid="login-submit-button">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 ml-1.5" /></>}
                        </Button>
                    </form>

                </div>
            </div>

            {/* Right: hero */}
            <div className="hidden lg:flex relative overflow-hidden items-center justify-center p-12 bg-gradient-to-br from-[#726AF0] via-[#5B5BF0] to-[#E64AA1]">
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)",
                    backgroundSize: "40px 40px"
                }} />
                <div className="relative z-10 max-w-md text-white">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-bold uppercase tracking-widest mb-6">
                        <span className="h-2 w-2 rounded-full bg-[#FFA81B]"></span>
                        Live cohort • Spring 2027
                    </div>
                    <h2 className="font-display text-5xl tracking-tight leading-tight">
                        Learn in cohort.<br />Build in public.<br />Earn the seal.
                    </h2>
                    <p className="mt-6 text-white/85 text-lg leading-relaxed">
                        Master Digital Marketing, Data Analytics & AI Automation with a curriculum built for India's offline & hybrid learners.
                    </p>
                    <div className="mt-10 grid grid-cols-3 gap-4">
                        {[["500+", "Active learners"], ["3", "Tracks"], ["98%", "Placement"]].map(([n, l]) => (
                            <div key={l}>
                                <div className="font-display text-3xl">{n}</div>
                                <div className="text-xs uppercase tracking-widest text-white/70 mt-1">{l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
